import { useCallback } from 'react';
import { useSeamlessAudioManager } from './useSeamlessAudioManager';
import { createAudioMixer } from '@/utils/audioMixingUtils';

interface EnhancedAudioManagerProps {
  mainAudioUrl?: string;
  backgroundAudioUrl?: string;
  backgroundVolume?: number;
  autoPlayBackground?: boolean;
  lazyLoad?: boolean;
}

export const useEnhancedAudioManager = ({
  mainAudioUrl,
  backgroundAudioUrl,
  backgroundVolume = 0.5,
  autoPlayBackground = true,
  lazyLoad = false
}: EnhancedAudioManagerProps) => {
  const seamlessManager = useSeamlessAudioManager({
    mainAudioUrl,
    backgroundAudioUrl,
    backgroundVolume,
    autoPlayBackground,
    lazyLoad
  });

  // Enhanced play function with seamless controls
  const playWithBackground = useCallback(async (playbackRate: number = 1) => {
    if (!mainAudioUrl) {
      return;
    }

    // Update playback rate before starting
    if (playbackRate !== 1) {
      seamlessManager.updatePlaybackRate(playbackRate);
    }

    // Start seamless playback
    await seamlessManager.play();
  }, [mainAudioUrl, seamlessManager]);

  // Enhanced stop function
  const stopAll = useCallback(() => {
    seamlessManager.stop();
  }, [seamlessManager]);


  // Get mixed audio blob for other uses (like email attachments)
  const getMixedAudioBlob = useCallback(async (): Promise<Blob | null> => {
    if (!mainAudioUrl || !backgroundAudioUrl) {
      return null;
    }

    try {
      const mixer = createAudioMixer();
      return await mixer.createMixedAudioBlob(
        mainAudioUrl,
        backgroundAudioUrl,
        {
          mainVolume: 1.0,
          backgroundVolume: backgroundVolume,
          outputFormat: 'wav'
        }
      );
    } catch (error) {
      return null;
    }
  }, [mainAudioUrl, backgroundAudioUrl, backgroundVolume]);

  return {
    // Enhanced functionality
    playWithBackground,
    stopAll,
    getMixedAudioBlob,

    // Seamless controls
    pause: seamlessManager.pause,
    seekTo: seamlessManager.seekTo,

    // Background-only controls
    stopBackgroundAudio: seamlessManager.stopBackgroundAudio,
    pauseBackgroundAudio: seamlessManager.pauseBackgroundAudio,
    resumeBackgroundAudio: seamlessManager.resumeBackgroundAudio,

    // State
    isPlaying: seamlessManager.isPlaying,
    isMainAudioReady: seamlessManager.isMainAudioReady,
    isBackgroundAudioReady: seamlessManager.isBackgroundAudioReady,
    isBackgroundAudioLoading: seamlessManager.isBackgroundAudioLoading,
    isBackgroundPlaying: seamlessManager.isBackgroundPlaying,
    isBackgroundMuted: seamlessManager.isBackgroundMuted,
    canCreateMixedAudio: !!(mainAudioUrl && backgroundAudioUrl && seamlessManager.isMainAudioReady),

    // Timing information
    currentTime: seamlessManager.currentTime,
    duration: seamlessManager.duration,

    // Volume and rate controls
    updateMainVolume: seamlessManager.updateMainVolume,
    updateBackgroundVolume: seamlessManager.updateBackgroundVolume,
    updatePlaybackRate: seamlessManager.updatePlaybackRate,

    // Background audio controls
    playBackgroundAudio: seamlessManager.playBackgroundAudio,
    initializeAudioContext: seamlessManager.initializeAudioContext
  };
};